import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import styles from './group-message-box.module.css'; // Use CSS modules

const SocialMessageBox = ({ group, onClose }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [userId, setUserId] = useState('');
    const [showOptions, setShowOptions] = useState(null);
    const [showReadBy, setShowReadBy] = useState(null);
    const bottomRef = useRef(null);
    const token = useMemo(() => localStorage.getItem('token'), []);

    useEffect(() => {
        const fetchMessages = async () => {
            try {
                const response = await axios.post('/api/group/get-group-messages', { groupId: group._id }, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                if (response.data.success) {
                    const filteredMessages = response.data.messages.filter(msg => !msg.deletedFor.includes(response.data.userId));
                    setMessages(filteredMessages);
                    setUserId(response.data.userId);
                    await axios.post('/api/group/mark-messages-read', {
                        groupId: group._id,
                        userId: response.data.userId
                    }, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                }
            } catch (error) {
                console.error('Error fetching group messages:', error);
            }
        };

        fetchMessages();
    }, [group._id]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;

        try {
            const response = await axios.post('/api/group/send-group-message', {
                groupId: group._id,
                senderId: userId,
                message: newMessage
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data.success) {
                setMessages((prevMessages) => [...prevMessages, response.data.message]);
                setNewMessage('');
            }
        } catch (error) {
            console.error('Error sending group message:', error);
        }
    };

    const handleDeleteMessage = async (messageId, forEveryone) => {
        try {
            await axios.post('/api/group/delete-group-message', {
                messageId,
                forEveryone
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setMessages((prevMessages) => prevMessages.filter(msg => msg._id !== messageId));
            setShowOptions(null)
        } catch (error) {
            console.error('Error deleting group message:', error);
        }
    };

    const formatDate = (date) => {
        const d = new Date(date);
        const now = new Date();
        const isThisYear = d.getFullYear() === now.getFullYear();
        const day = d.getDate().toString().padStart(2, '0');
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const year = d.getFullYear().toString().slice(-2);
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
        return isThisYear ? `${hours}:${minutes} ${day}/${month}` : `${hours}:${minutes} ${day}/${month}/${year}`;
    };

    const handleClickOutside = (event) => {
        if (!event.target.closest(`.${styles.messageOptionsButton}`) && !event.target.closest(`.${styles.messageOptionsMenu}`)) {
            setShowOptions(null);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    return (
        <div className={styles.groupChatPopup}>
            <div className={styles.groupChatHeader}>
                <h3>{group.name}</h3>
                <button onClick={onClose}>×</button>
            </div>
            <div className={styles.groupMessageBox}>
                {messages.map((msg, index) => (
                    <div key={index} className={`${styles.message} ${msg.senderId === userId ? styles.sent : styles.received}`}>
                        <div className={styles.messageContent}>
                            {msg.message}
                            <button className={styles.messageOptionsButton} onClick={() => setShowOptions(showOptions === index ? null : index)}>⋮</button>
                            {showOptions === index && (
                                <div className={styles.messageOptionsMenu}>
                                    <button onClick={() => {setShowReadBy(showReadBy === index ? null : index); setShowOptions(null);} }>Read by</button>
                                    <button onClick={() => handleDeleteMessage(msg._id, false)}>Delete for me</button>
                                    {msg.senderId === userId && (
                                        <button onClick={() => handleDeleteMessage(msg._id, true)}>Delete for everyone</button>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className={styles.messageTimestamp}>
                            {formatDate(msg.createdAt)}
                        </div>
                        {showReadBy === index && msg.readBy && msg.readBy.length > 0 && (
                            <div className={styles.messageReadRecipients}>
                                Read by: {msg.readBy.map(recipient => (
                                    <span key={recipient.userId}>                                        
                                        <img src={recipient.userId.profilePicture ? recipient.userId.profilePicture.replace('/upload/', '/upload/w_100,h_100,c_fill/') : '/default-profile.png'}
                                             alt={recipient.userId.name} 
                                             className={styles.recipientProfilePicture} />
                                        {recipient.userId.name}
                                        {recipient.userId}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
                <div ref={bottomRef} />
            </div>
            <div className={styles.inputBox}>
                <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                />
                <button onClick={handleSendMessage}>Send</button>
            </div>
        </div>
    );
};

export default SocialMessageBox;